import {Form as AntForm} from 'antd';
import {FormInstance} from 'antd/es/form';
import {ColProps} from 'antd/es/grid';
import cn from 'classnames';
import React, {useMemo, FC, Key} from 'react';

import {ExternalSystemsSettings} from 'components/@common/specific/external-systems-settings';
import {useFieldPropertySettings} from 'components/form/field/hooks/use-field-property-settings';
import {IFieldMeta, EFieldType} from 'entities/metadata';
import {IFormEntityData, IDatePickerState} from 'modules/data/data-types';
import {convertWidthData} from 'shared/utils/convert-width-data';

import {FieldTooltip} from '../field-tooltip';
import {fieldDefaultWidthResolver, fieldTypeResolver} from '../field-type-resolver';
import {EFormMode} from '../form-types';
import {useDefaultValueByRule} from '../hooks/field-default-value.hook';
import {useFormWatcher} from '../hooks/use-form-watcher';
import {CustomSelect} from '../inputs/custom-select';
import {fieldValidationResolver} from '../utils/field-validation-resolver';
import {FieldLabel} from './field-label/field-label';
import {useFieldDisabling} from './hooks/use-field-disabling';
import {useFieldLabel} from './hooks/use-field-label';
import {getFieldInitialValue} from './utils/get-field-initial-value';
import {shouldPerformFieldUpdate} from './utils/should-perform-field-update';

interface IFieldProps {
    formData?: IFormEntityData;
    entityName?: string;
    parentEntityName?: string;
    hideLabel?: boolean;
    fieldMeta: IFieldMeta;
    form?: FormInstance;
    fieldKey?: Key | Key[];
    name?: Key | Key[];
    wrapperCol?: ColProps;
    labelCol?: ColProps;
    placeholder?: string;
    editModeWidth?: boolean;
    formMode?: EFormMode;
    datePickerStates?: IDatePickerState;
    isDisableFields?: boolean;
    fieldLabel?: string;
    fieldClassNames?: cn.Argument;
    innerClassNames?: cn.Argument;
    areProgramSetProgramsUnique?: boolean;
}
export const Field: FC<IFieldProps> = ({
    fieldMeta,
    formData,
    entityName,
    parentEntityName,
    form,
    hideLabel = false,
    fieldKey,
    name,
    wrapperCol = {},
    labelCol,
    innerClassNames,
    editModeWidth,
    isDisableFields,
    formMode,
    datePickerStates,
    fieldLabel,
    fieldClassNames,
    areProgramSetProgramsUnique,
    ...props
}: IFieldProps) => {
    const dependencies: string[] = [];
    const {
        validationRules, requiredMatchPasswordField, propertyCode, propertyCodeList,
    } = fieldMeta;

    const {propertySettings, regularFromProperty} = useFieldPropertySettings({
        validationRules,
        propertyCodeList,
        propertyCode,
    });

    const convertedEditModeWidth = convertWidthData(fieldMeta?.editModeWidth);
    const convertedWidth = convertWidthData(fieldMeta?.width);
    const width: string | undefined = (editModeWidth && convertedEditModeWidth) || convertedWidth;
    const initialValue = formData?.id ? formData.data[fieldMeta.key] : undefined;

    const {
        isHidden: isHiddenByWatcher,
        isRequired: isRequiredByWatcher,
        isDisabled: isDisabledByWatcher,
        isAllowedEmpty: isAllowedEmptyByWatcher,
        validationRules: validationRulesByWatcher,
        referenceUrlByWatcher,
        shouldPerformWatcherTargetsUpdate,
        dependentFieldValue,
        disabledDateConfig,
    } = useFormWatcher({
        fieldMeta, form, formMode, entityName,
    });

    const {handleDisabledField} = useFieldDisabling({entityName, ...fieldMeta});

    const {label} = useFieldLabel({
        type: fieldMeta?.type,
        label: fieldMeta?.label,
        hideLabel: hideLabel || fieldMeta?.hideLabel,
    });

    if (requiredMatchPasswordField) {
        dependencies.push(requiredMatchPasswordField);
    }
    const {getDefaultValue} = useDefaultValueByRule(entityName || '', fieldMeta);

    const Component = useMemo(() => fieldTypeResolver(
        fieldMeta,
        getDefaultValue,
        formMode,
        formData,
        form,
        entityName,
        parentEntityName,
        handleDisabledField,
        isDisableFields || isDisabledByWatcher,
        datePickerStates,
        dependentFieldValue,
        areProgramSetProgramsUnique,
        disabledDateConfig,
    ), [fieldMeta, form, formData, parentEntityName, isDisabledByWatcher, dependentFieldValue, disabledDateConfig]);

    const formItemLayout = !fieldMeta.label ? {
        wrapperCol,
        labelCol,
    } : {
        wrapperCol,
        labelCol,
    };

    if (
        [EFieldType.LIST, EFieldType.GROUP].includes(fieldMeta.type)
    ) {
        return (
            <Component
                rows={4}
                form={form}
                field={fieldMeta}
                name={name}
                {...props}
            />
        );
    }

    if (!formData?.id && fieldMeta.type === EFieldType.BOOLEAN_DELETE) {
        return null;
    }

    const handleResetDependentValidation = () => {
        if (form?.getFieldValue(fieldMeta.reverseDependencyTo ?? '')) {
            form.validateFields([fieldMeta.reverseDependencyTo ?? '']);
        }
    };

    if (fieldMeta.type === EFieldType.EXTERNAL_SYSTEMS_MODAL) return <ExternalSystemsSettings />;
    return (
        <div className={cn('field', (isHiddenByWatcher || fieldMeta.isHidden) && 'no-display', fieldClassNames)}>
            <AntForm.Item
                noStyle
                shouldUpdate={(
                    prevValues, currentValues,
                ) => shouldPerformWatcherTargetsUpdate(prevValues, currentValues)
                    || shouldPerformFieldUpdate(prevValues, currentValues, fieldMeta?.referenceParamsKeys)
                    || false}
            >
                {({getFieldValue}) => (
                    <AntForm.Item
                        rules={fieldValidationResolver({
                            validationRules: validationRules || [],
                            fieldMeta,
                            validationRulesByWatcher: validationRulesByWatcher || [],
                            formData,
                            initialValue,
                            form,
                            requiredMatchPasswordField,
                            propertyCode,
                            regularFromProperty,
                            isRequiredByWatcher,
                            isAllowedEmptyByWatcher,
                            propertySettings,
                            formMode,
                        })}
                        label={fieldLabel ?? (label ? <FieldLabel {...{fieldMeta, label}} /> : '')}
                        name={name ?? fieldMeta.key}
                        key={fieldMeta.key}
                        fieldKey={fieldKey}
                        {...formItemLayout}
                        style={{width: width || fieldDefaultWidthResolver(fieldMeta.type)}}
                        dependencies={dependencies}
                        initialValue={getFieldInitialValue({...fieldMeta})}
                        className={cn(innerClassNames,
                            !label && 'without-label',
                            fieldMeta.isNotDisplay && 'not-display')}
                    >
                        {
                            ({
                                // todo: нужен глобальный рефакторинг fieldTypeResolver
                                // компоненты делают mount/unmount несколько раз при изменении стейта выше
                                [EFieldType.CUSTOM_SELECT]: <CustomSelect
                                    settings={{
                                        isDisabled: fieldMeta.isDisabled || isDisabledByWatcher,
                                        url: referenceUrlByWatcher,
                                        formFieldKey: fieldMeta.key,
                                        formInstance: form,
                                    }}
                                    {...{fieldMeta}}
                                />,
                            } as any)[fieldMeta.type] ?? (
                                <Component
                                    rows={4}
                                    form={form}
                                    parentEntityName={parentEntityName}
                                    {...props}
                                    onChange={fieldMeta.reverseDependencyTo
                                        ? handleResetDependentValidation : undefined}
                                    referenceParamsDefault={fieldMeta.referenceParamsDefault}
                                    referenceParams={fieldMeta.referenceParamsKeys
                                        ?.reduce((acc: Record<string, any>, currentKeys: any) => {
                                            const value = getFieldValue(currentKeys.from);
                                            if (currentKeys.to && currentKeys.from && value) {
                                                acc[currentKeys.to] = value;
                                            }
                                            return acc;
                                        }, {}) || {}}
                                />
                            )
                        }

                    </AntForm.Item>
                )}
            </AntForm.Item>
            {fieldMeta?.tooltip && (
                <FieldTooltip
                    meta={fieldMeta.tooltip}
                />
            )}
        </div>
    );
};
